import { useSWRData } from "@root/shared/hooks/useSWRData";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { convertToTermAndDescriptionsList } from "@clientFrontend/features/scout/utils/convertToTermAndDescriptionsList";
import {
  buildMissingUserDetailDataError,
  extractUserDetailData,
} from "@clientFrontend/features/scout/utils/userDetailResponse";
import { useEffect, useMemo } from "react";
import type { UserInformation } from "@clientFrontend/features/scout/types/userInformation";
import type { PickupButtonContentProps } from "@clientFrontend/features/scout/components/UserDetailModal/uiParts/Header";
import type { UserHiddenButtonContent } from "@clientFrontend/features/scout/types/userHidden";
import type { UserDetailModalProps } from "./Presentation";

type gaEvents = {
  modal: {
    gaAction: string;
    gaLabel: string;
  };
  scout: {
    gaAction: string;
    gaLabel: string;
  };
  premiumScout: {
    gaAction: string;
    gaLabel: string;
  };
  pickup: {
    gaAction: string;
    gaLabel: string;
  };
  pickupRelease: {
    gaAction: string;
    gaLabel: string;
  };
};

type UserDetailModalInformation = {
  gaEvents: gaEvents;
} & UserInformation;

export const useUserDetailModal = (
  fetchUrl: string,
  // NOTE:スカウト送信画面ではピックアップ機能は付かないため
  pickupButtonContent?: PickupButtonContentProps,
  userHiddenButtonContent?: UserHiddenButtonContent,
): UserDetailModalProps | null => {
  const { data: items } = useSWRData<unknown>({
    url: fetchUrl,
    options: { onError: (error: Error) => sendErrorLog(error) },
  });

  const data = useMemo(() => extractUserDetailData<UserDetailModalInformation>(items), [items]);

  // NOTE: 取得前（undefined）は対象外とし、レスポンスがあるのにdataを取り出せない場合のみ通知する
  const isMissingData = items !== undefined && data === null;
  useEffect(() => {
    if (!isMissingData) return;

    sendErrorLog(buildMissingUserDetailDataError(fetchUrl, items));
  }, [isMissingData, fetchUrl, items]);

  return useMemo(() => {
    if (!data) return null;

    const userSchoolInfoList = convertToTermAndDescriptionsList(data.userSchools);
    const userCareerInfoList = convertToTermAndDescriptionsList(data.userCareers);
    const userAchievementsInfoList = convertToTermAndDescriptionsList(data.userAchievements);

    return {
      gaEvents: data.gaEvents,
      displayControlInformation: {
        isHideInfo: data.basicInfo.isHideInfo,
        hideInfoReasonText: data.basicInfo.hideInfoReasonText,
        notScoutableReasonText: data.basicInfo.notScoutableReasonText,
        isNotScoutable: data.basicInfo.isNotScoutable,
        scoutIndexUrl: data.basicInfo.scoutIndexUrl,
        isNew: data.basicInfo.isNew,
        entryText: data.basicInfo.entryText,
        isIconFire: data.basicInfo.isIconFire,
      },
      userData: {
        sexAndAgeText: data.basicInfo.sexAndAgeText,
        acceptableReceivedScoutCount: data.basicInfo.acceptableReceivedScoutCount,
        currentAccessAt: data.basicInfo.currentAccessAt,
        multiActionInfo: data.basicInfo.multiActionInfo,
        photoContestInfo: data.basicInfo.photoContestInfo,
      },
      historyInformation: {
        userSchoolInfoList,
        userCareerInfoList,
        userAchievementsInfoList,
        activityHistories: data.activityHistories,
      },
      hopedConditions: {
        basicHopedConditions: data.basicHopedConditions,
        detailHopedConditions: data.detailHopedConditions,
      },
      userProfileInformation: {
        thoughtsAndSelfPr: data.thoughtsAndSelfPr,
        userSkills: data.userSkills,
        userProductPhotos: data.userProductPhotos,
      },
      pickupButtonContent,
      userHiddenButtonContent: userHiddenButtonContent || undefined,
      scoutButtonContent: data.scoutButtonContent,
    };
  }, [data, pickupButtonContent, userHiddenButtonContent]);
};
